import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { sanitizeVariableName, sanitizeVariableValue, safeReplaceVariables } from '../utils/security';

const STORAGE_KEY = 'ai-flow-global-vars';

const VariableContext = createContext();

export const useVariables = () => {
  const context = useContext(VariableContext);
  if (!context) {
    throw new Error('useVariables must be used within a VariableProvider');
  }
  return context;
};

export const VariableProvider = ({ children }) => {
  const [globalVariables, setGlobalVariables] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [localVariables, setLocalVariables] = useState({});

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(globalVariables));
    } catch { /* storage full or unavailable */ }
  }, [globalVariables]);
  const [variableHistory, setVariableHistory] = useState([]);

  // Set a variable (global or local)
  const setVariable = useCallback((name, value, scope = 'local', nodeId = null) => {
    // Sanitize inputs
    const sanitizedName = sanitizeVariableName(name);
    const sanitizedValue = sanitizeVariableValue(value);

    if (!sanitizedName) {
      console.warn('Invalid variable name:', name);
      return;
    }

    const timestamp = new Date().toISOString();
    const variableData = {
      name: sanitizedName,
      value: sanitizedValue,
      scope,
      nodeId,
      timestamp,
      type: typeof sanitizedValue,
    };

    if (scope === 'global') {
      setGlobalVariables(prev => ({
        ...prev,
        [sanitizedName]: variableData
      }));
    } else {
      setLocalVariables(prev => ({
        ...prev,
        [`${nodeId}_${sanitizedName}`]: variableData
      }));
    }

    // Add to history
    setVariableHistory(prev => [...prev.slice(-99), variableData]); // Keep last 100 entries
  }, []);

  // Get a variable value
  const getVariable = useCallback((name, nodeId = null) => {
    // First check local variables for this node
    if (nodeId) {
      const localKey = `${nodeId}_${name}`;
      if (localVariables[localKey]) {
        return localVariables[localKey].value;
      }
    }

    // Then check global variables
    if (globalVariables[name]) {
      return globalVariables[name].value;
    }

    return undefined;
  }, [globalVariables, localVariables]);

  // Get all variables
  const getAllVariables = useCallback(() => {
    return {
      global: globalVariables,
      local: localVariables,
    };
  }, [globalVariables, localVariables]);

  // Clear variables
  const clearVariables = useCallback((scope = 'all') => {
    if (scope === 'global' || scope === 'all') {
      setGlobalVariables({});
    }
    if (scope === 'local' || scope === 'all') {
      setLocalVariables({});
    }
    if (scope === 'all') {
      setVariableHistory([]);
    }
  }, []);

  // Delete a single variable by name and scope
  const deleteVariable = useCallback((name, scope = 'global', nodeId = null) => {
    if (scope === 'global') {
      setGlobalVariables(prev => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    } else {
      // Local variables are keyed by `${nodeId}_${name}`; delete all entries matching name if nodeId not provided
      setLocalVariables(prev => {
        const copy = { ...prev };
        Object.keys(copy).forEach((key) => {
          const [_node, ...nm] = key.split('_');
          const kname = nm.join('_');
          const nodeMatch = nodeId ? key.startsWith(`${nodeId}_`) : true;
          if (nodeMatch && kname === name) delete copy[key];
        });
        return copy;
      });
    }
  }, []);

  // Replace variables in text (template engine) - SECURE VERSION
  const replaceVariables = useCallback((text, nodeId = null) => {
    if (!text || typeof text !== 'string') return text;

    // Use secure replacement function
    const allVars = { ...globalVariables, ...localVariables };
    const varValues = {};

    // Build safe variable map
    Object.entries(allVars).forEach(([key, data]) => {
      varValues[data.name] = data.value;
      if (key.includes('_')) {
        const [keyNodeId, varName] = key.split('_', 2);
        if (keyNodeId === nodeId) {
          varValues[varName] = data.value;
        }
      }
    });

    return safeReplaceVariables(text, varValues, nodeId);
  }, [globalVariables, localVariables]);

  // Get variable suggestions for autocomplete
  const getVariableSuggestions = useCallback(() => {
    const suggestions = [];

    // Add global variables
    Object.keys(globalVariables).forEach(name => {
      suggestions.push({
        name,
        scope: 'global',
        type: globalVariables[name].type,
        value: globalVariables[name].value,
      });
    });

    // Add unique local variable names
    const localNames = new Set();
    Object.keys(localVariables).forEach(key => {
      const name = key.split('_').slice(1).join('_'); // Remove nodeId prefix
      if (!localNames.has(name)) {
        localNames.add(name);
        suggestions.push({
          name,
          scope: 'local',
          type: localVariables[key].type,
          value: localVariables[key].value,
        });
      }
    });

    return suggestions;
  }, [globalVariables, localVariables]);

  // Slot filling - extract variables from user input
  const extractSlots = useCallback((input, slots = []) => {
    const extracted = {};

    slots.forEach(slot => {
      const { name, pattern, required = false } = slot;

      if (pattern) {
        const regex = new RegExp(pattern, 'i');
        const match = input.match(regex);
        if (match) {
          extracted[name] = match[1] || match[0];
        }
      }
    });

    return extracted;
  }, []);

  // Check if all required slots are filled
  const validateSlots = useCallback((slots, nodeId = null) => {
    const missing = [];

    slots.forEach(slot => {
      if (slot.required) {
        const value = getVariable(slot.name, nodeId);
        if (value === undefined || value === null || value === '') {
          missing.push(slot.name);
        }
      }
    });

    return {
      isValid: missing.length === 0,
      missing,
    };
  }, [getVariable]);

  // Expose delete for UI events that cannot import context directly (temporary glue)
  if (typeof window !== 'undefined') {
    window.__aiFlowDeleteVar = deleteVariable;
  }

  const value = {
    // State
    globalVariables,
    localVariables,
    variableHistory,

    // Actions
    setVariable,
    getVariable,
    getAllVariables,
    clearVariables,
    deleteVariable,
    replaceVariables,
    getVariableSuggestions,
    extractSlots,
    validateSlots,
  };

  return (
    <VariableContext.Provider value={value}>
      {children}
    </VariableContext.Provider>
  );
};
